import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { BulkImportType } from '../../../../../types';
import { Dispatch, SetStateAction } from 'react';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import { selectAdvancedPath, selectGuidedPath } from './path-selection';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import SharedButton from '../../../../shared/buttons/shared-button';
import SharedFilePicker from '../../../../shared/shared-file-picker';
import SharedFilePickerOld from '../../../../shared/shared-file-picker-old';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import OptionCard from './option-card';
import SelectedFileRow from './selected-file-row';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import FeedOutlined from '@mui/icons-material/FeedOutlined';
import UploadOutlined from '@mui/icons-material/UploadOutlined';

const requiredColumnLinks = [
  {
    label: 'Bulk Group Updates',
    href: 'https://promevo.com/knowledge/bulk-operation-group-operations#advanced-path',
  },
  {
    label: 'Bulk User Updates',
    href: 'https://promevo.com/knowledge/bulk-operation-user-profile#guide-me-path',
  },
  {
    label: 'Bulk Shared Contacts Updates',
    href: 'https://promevo.com/knowledge/bulk-operation-shared-contact#advanced-path',
  },
];

const guidedExportSteps = [
  'Choose the columns and sources to export',
  'gPanel creates a Google Sheet with your current data',
  'Make your changes in the sheet',
  'Process the changes from the Bulk Operations list',
];

interface StepPathSelectionProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  control: Control<BulkOpsFormValues>;
  selectedBulkImportType: BulkImportType | null;
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  watch: UseFormWatch<BulkOpsFormValues>;
  spreadSheetName: string;
  errors: FieldErrors<BulkOpsFormValues>;
  onContinue: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function StepPathSelection({
  setValue,
  control,
  selectedBulkImportType,
  setSpreadSheetName,
  watch,
  spreadSheetName,
  errors,
  onContinue,
}: StepPathSelectionProps) {
  const theme = useTheme();
  const { features } = useUserSettings();
  const path = watch('path');
  const spreadsheetId = watch('spreadsheetId');
  const hasAdvancedFile = path === 'advanced' && Boolean(spreadsheetId);
  const FilePicker = isFeatureEnabled('drivePickerUIReact', features)
    ? SharedFilePicker
    : SharedFilePickerOld;
  const iconSx = { fontSize: 27, color: theme.palette.semantic.icon.primary };

  const handleFileRemoved = () => {
    setValue('spreadsheetId', '');
    setSpreadSheetName('');
  };

  return (
    <Stack gap={3} width={'100%'}>
      <FormSectionHeader
        descriptionVariant="body2"
        description="How do you want to set up this bulk operation?"
      >
        Select Configuration
      </FormSectionHeader>
      <Stack flexDirection={'row'} gap={4} alignItems="stretch" width={'100%'}>
        <OptionCard
          icon={<FeedOutlined sx={iconSx} />}
          title="Guided Export"
          bestFor="Best for: Making changes to your current data"
          actions={
            <SharedButton
              variant="primary"
              onClick={(event) => {
                selectGuidedPath(
                  setValue,
                  selectedBulkImportType,
                  setSpreadSheetName
                );
                onContinue(event);
              }}
            >
              Start Guided Export
            </SharedButton>
          }
        >
          <p>
            Export your current users, groups, or shared contacts to a Google
            Sheet, edit it, then process your changes.
          </p>
          <ol>
            {guidedExportSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </OptionCard>
        <OptionCard
          icon={<UploadOutlined sx={iconSx} />}
          title="Advanced Operation"
          bestFor="Best for: When you already have a sheet ready"
          selected={hasAdvancedFile}
          actions={
            <Stack gap={3} sx={{ width: '100%' }}>
              <Controller
                name="spreadsheetId"
                control={control}
                render={({ field }) => (
                  <FilePicker
                    pickerType="spreadsheet"
                    selectedFileId={field.value || ''}
                    selectedFileName={spreadSheetName}
                    onFileSelected={({ id, name }) => {
                      selectAdvancedPath(
                        setValue,
                        selectedBulkImportType,
                        setSpreadSheetName
                      );
                      setValue('spreadsheetId', id);
                      setSpreadSheetName(name);
                    }}
                    onFileRemoved={handleFileRemoved}
                    errorMessage={errors.spreadsheetId?.message}
                    includeSharedDrives
                    customTrigger={(openPicker) => (
                      <SharedButton
                        variant={hasAdvancedFile ? 'secondary' : 'primary'}
                        onClick={openPicker}
                        sx={{ mb: errors.spreadsheetId ? 1 : 0 }}
                      >
                        {hasAdvancedFile ? 'Change File' : 'Select File'}
                      </SharedButton>
                    )}
                  />
                )}
              />
              {hasAdvancedFile && (
                <SelectedFileRow
                  name={spreadSheetName}
                  onRemove={handleFileRemoved}
                  action={
                    <SharedButton variant="primary" onClick={onContinue}>
                      Continue
                    </SharedButton>
                  }
                />
              )}
            </Stack>
          }
        >
          <p>
            Select a Google Sheet you’ve already created or edited, and gPanel
            will start processing it right away. Use it for things like adding
            members to groups or adding nicknames to users.
          </p>
          <Box sx={{ mt: 2 }}>
            <p>See required columns for:</p>
            <ul>
              {requiredColumnLinks.map(({ label, href }) => (
                <li key={label}>
                  <Link href={href} target="_blank" rel="noreferrer noopener">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </Box>
        </OptionCard>
      </Stack>
    </Stack>
  );
}
